import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useFormContext, useController } from "react-hook-form";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";
import CustomTooltip from "../../components/common/CustomTooltip";
import { sortOptionsAlphabeticallyOtherLast } from "../../utils/helper";

interface Option {
  Id: string | number;
  Name: string;
}

interface SelectFieldProps {
  name: string;
  label: string;
  isAsterisk?: boolean;
  options: Option[];
  required?: boolean;
  requiredMessage?: string;
  placeholderText?: string;
  value?: string | number;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;

  /** NEW PROP */
  isSearchable?: boolean;

  /** Optional content rendered inline after the label + asterisk (e.g. an info-icon trigger). */
  labelAddon?: React.ReactNode;
}

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  isAsterisk = true,
  required = false,
  options,
  requiredMessage = "Please select an option",
  placeholderText = "Select an option",
  value,
  className = "",
  onChange,
  disabled,

  /** NEW PROP */
  isSearchable,

  labelAddon,
}) => {
  // For testing
  // options = options.slice(0, 6);

  // if options length is greater than 7 then make isSearchable true
  isSearchable = false;
  if (options.length > 7) {
    isSearchable = true;
  }

  const sortedOptions = useMemo(
    () => sortOptionsAlphabeticallyOtherLast(options, (o) => o.Name),
    [options],
  );

  const {
    control,
    formState: { errors, isSubmitted },
    trigger,
  } = useFormContext();

  const {
    field,
    fieldState: { error: fieldError, isTouched },
  } = useController({
    name,
    control,
    rules: { required: required ? requiredMessage : false },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // This is a custom dropdown, not a native element, so it never fires a
  // real DOM blur event — `field.onBlur()` never runs on its own. Call this
  // explicitly whenever the user finishes interacting with it (selects an
  // option, or closes it without selecting) so `fieldState.isTouched`
  // actually flips and validation runs regardless of the parent form's
  // `mode` (most forms use the RHF default "onSubmit", which never
  // validates pre-submit — and Save stays disabled while the form is
  // invalid, so that submit can never happen).
  const markTouched = () => {
    field.onBlur();
    trigger(name);
  };

  // Autofocus search box ONLY when searchable
  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isSearchable]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpenRef.current) {
          markTouched();
        }
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filtering ONLY when isSearchable = true
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchTerm.trim()) return sortedOptions;
    const term = searchTerm.toLowerCase();
    return sortedOptions.filter((opt) => opt.Name.toLowerCase().includes(term));
  }, [sortedOptions, searchTerm, isSearchable]);

  const getSelectedLabel = (selectedValue: string | number) => {
    const selected = options.find(
      (opt) =>
        opt?.Id != null &&
        selectedValue != null &&
        String(opt.Id) === String(selectedValue),
    );
    return selected ? selected.Name : placeholderText;
  };

  const selectedLabelRef = useRef<HTMLSpanElement>(null);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);
  const selectedLabel = getSelectedLabel(value ?? field.value);

  useLayoutEffect(() => {
    // Read the ref on every measure: toggling the tooltip wrapper remounts the span.
    const measure = () => {
      const el = selectedLabelRef.current;
      setIsLabelTruncated(!!el && el.scrollWidth > el.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [selectedLabel]);

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <div className="d-flex align-items-center gap-2">
          <label className={TextCss.black.sub_heading_600_14}>
            {label} {isAsterisk && <span className="text-danger">*</span>}
          </label>
          {labelAddon}
        </div>
      )}

      {(() => {
        const selectedValue = value ?? field.value;
        const showErrorMessage =
          (isTouched || isSubmitted || fieldError?.type === "maxLength") &&
          fieldError;

        return (
          <div className="custom-select-container" ref={dropdownRef}>
            {/* Trigger */}
            <div
              className={`custom-select-trigger form-control
    ${isOpen ? "open" : ""}
    ${(() => {
      const parts = name.split(".");
      if (parts.length === 3 && parts[0] === "payments") {
        const index = parseInt(parts[1], 10);
        if (!isNaN(index)) {
          return errors.payments?.[index]?.PaymentType &&
            (isTouched ||
              isSubmitted ||
              errors.payments?.[index]?.PaymentType?.type === "maxLength")
            ? "is-invalid"
            : "";
        }
      }
      return showErrorMessage ? "is-invalid" : "";
    })()}
    ${disabled ? "disabled" : ""}`}
              onClick={() => {
                if (disabled) return;
                const next = !isOpen;
                setIsOpen(next);
                if (!next) {
                  markTouched();
                }
              }}
            >
              <CustomTooltip
                content={selectedLabel}
                disabled={!isLabelTruncated || isOpen}
              >
                <span
                  ref={selectedLabelRef}
                  className={field.value ? "selected" : "placeholder"}
                  style={{ paddingRight: "25px" }}
                >
                  {selectedLabel}
                </span>
              </CustomTooltip>

              <div className={`select-arrow ${isOpen ? "rotate" : ""}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
              <div className="custom-select-dropdown">
                {/* Search bar (ONLY when isSearchable = true) */}
                {isSearchable && (
                  <div
                    className="custom-select-search"
                    style={{ padding: "8px" }}
                  >
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="custom-select-search-input"
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid var(--Grey-Subtext2)",
                        borderRadius: "6px",
                        backgroundColor: "var(--bg-secondary)",
                        color: "var(--Grey-Heading)",
                      }}
                    />
                  </div>
                )}

                <div className="custom-select-options">
                  {filteredOptions.length === 0 ? (
                    <div className="custom-select-option disabled">
                      No options found
                    </div>
                  ) : (
                    filteredOptions.map((option, index) => (
                      <React.Fragment key={option.Id}>
                        <div
                          className={`custom-select-option ${
                            selectedValue?.toString() === option.Id.toString()
                              ? "selected"
                              : ""
                          }`}
                          title={option.Name}
                          onClick={() => {
                            field.onChange(option.Id);

                            if (onChange) {
                              const syntheticEvent = {
                                target: { value: option.Id.toString(), name },
                              } as React.ChangeEvent<HTMLSelectElement>;
                              onChange(syntheticEvent);
                            }

                            setIsOpen(false);
                            setSearchTerm("");
                            markTouched();
                          }}
                        >
                          {option.Name}
                        </div>

                        {index < filteredOptions.length - 1 && (
                          <div className="option-divider" />
                        )}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Hidden native select */}
            <select
              {...field}
              value={selectedValue ?? ""}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e);
              }}
              disabled={disabled}
              style={{ display: "none" }}
            >
              <option value="">{placeholderText}</option>
              {sortedOptions.map((option) => (
                <option key={option.Id} value={option.Id}>
                  {option.Name}
                </option>
              ))}
            </select>
          </div>
        );
      })()}

      {/* Dropdown ke bahar – bilkul pehle jaisi jagah pe */}
      {(() => {
        const parts = name.split(".");
        if (parts.length === 3 && parts[0] === "payments") {
          const index = parseInt(parts[1], 10);
          if (!isNaN(index)) {
            const paymentTypeError = errors.payments?.[index]?.PaymentType;
            if (paymentTypeError && (isTouched || isSubmitted)) {
              return (
                <div className="invalid-feedback d-block">
                  {paymentTypeError.message}
                </div>
              );
            }
          }
        }
        // normal field ke liye — fieldError/isTouched now come from this
        // component's own useController, so they're available here too.
        return fieldError && (isTouched || isSubmitted) ? (
          <div className="invalid-feedback d-block">
            {fieldError.message}
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default SelectField;
